import { useCallback, useMemo, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { shouldShowFlightDealBanner } from 'agentHub/selectors/agentHubSelectors'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { sortBy } from 'lib/array/arrayUtils'
import { normalizeAirportCode } from 'lib/flights/popularFlightUtils'
import TextLink from 'LuxKit/TextLink'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { formatFlightDealTravelPeriods } from 'components/OfferPage/FlightDeal/formatFlightDealTravelPeriods'
import Group from 'components/utils/Group'
import OfferCalendarHeaderExtensionContainer from './OfferCalendarHeaderExtensionContainer'

const BannerLayout = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: ${rem(20)};
  width: 100%;
`

const DealSummary = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  min-width: 0;
`

const DesktopSupportingText = styled.div`
  min-width: 0;
`

interface Props {
  airport?: App.Airport
  offer: App.Offer
  isFlightsActive: boolean
  wrapInHeaderExtensionContainer?: boolean
}

function OfferCalendarDatePickerFlightDealBanner(props: Props) {
  const { airport, offer, isFlightsActive, wrapInHeaderExtensionContainer } = props
  const intl = useIntl()
  const { fareTypeLabelMap } = useFlightFareLabels()
  const offerPageDispatch = useOfferPageDispatch()
  const shouldShowBanner = shouldShowFlightDealBanner(offer)
  const originAirportCode = airport?.code ? normalizeAirportCode(airport.code) : undefined
  const flightDealFilters = useMemo(() => ({
    originAirportCode,
    destinationAirportCode: offer.flightDestinationPort,
  }), [offer.flightDestinationPort, originAirportCode])
  const canFetchFlightDeals = shouldShowBanner &&
    !!originAirportCode &&
    !!offer.flightDestinationPort
  const [deals] = useFlightDeals('all', flightDealFilters, { disabled: !canFetchFlightDeals })

  const activeDeal = useMemo(() => {
    return sortBy(deals, (deal) => deal.perAdultPrice, 'asc')[0]
  }, [deals])
  const canShowFlightDealBanner = shouldShowBanner &&
    !!originAirportCode &&
    !!offer.flightDestinationPort &&
    !!activeDeal

  const travelPeriodsText = useMemo(() => {
    if (!activeDeal?.travelPeriods) return ''
    return formatFlightDealTravelPeriods(activeDeal.travelPeriods, intl)
  }, [activeDeal?.travelPeriods, intl])

  const handleToggleFlights = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setFlights, flights: true })
  }, [offerPageDispatch])

  const renderStrong = useCallback((chunks: ReactNode) => (
    <strong key="flight-deal-highlight">{chunks}</strong>
  ), [])

  if (!canShowFlightDealBanner) {
    return null
  }

  const toggleFlightsText = intl.formatMessage({
    id: '4vOqaUPx4laAeny409ko',
    defaultMessage: 'Toggle flights on to see sale dates',
    description: 'Link text prompting user to enable flights to see sale dates',
  })

  const banner = (
    <>
      <CSSBreakpoint min="desktop">
        <BannerLayout>
          <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />
          <DealSummary>
            {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
            <BodyText variant="medium">
              <FormattedMessage
                id="eNUS_9L-3ZdcpI5uaMud"
                defaultMessage="{carrierName} {origin}-{destination} {fareType} from <strong>{price}/adult</strong>"
                description="Calendar date picker flight deal banner combining airline, route airport codes, fare type, and formatted per-adult price"
                values={{
                  carrierName: activeDeal.carrier.name,
                  origin: originAirportCode,
                  destination: offer.flightDestinationPort,
                  fareType: fareTypeLabelMap[activeDeal.fareType],
                  price: <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />,
                  strong: renderStrong,
                }}
              />
            </BodyText>
          </DealSummary>
          {isFlightsActive && travelPeriodsText && (
            <DesktopSupportingText>
              <BodyText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="fBuVDFqsfwV4bsNc1RSI"
                  defaultMessage="Travel on select dates between <strong>{travelPeriods}</strong>"
                  description="Travel period information for flight deal on tablet view"
                  values={{
                    travelPeriods: travelPeriodsText,
                    strong: renderStrong,
                  }}
                />
              </BodyText>
            </DesktopSupportingText>
          )}
          {!isFlightsActive && (
            <DesktopSupportingText>
              <TextLink size="small" colour="light-foreground-alt" weight="regular" onClick={handleToggleFlights}>
                {toggleFlightsText}
              </TextLink>
            </DesktopSupportingText>
          )}
        </BannerLayout>
      </CSSBreakpoint>
      <CSSBreakpoint max="tablet">
        <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={8} fullWidth>
          <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />
          {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
          <Group direction="vertical" gap={0}>
            <BodyText variant="medium">
              <FormattedMessage
                id="fGAuasCQ6M7CzSwD-wlx"
                defaultMessage="{fareType} from <strong>{price}/adult</strong>"
                description="Flight deal pricing showing fare type and per-adult price on mobile"
                values={{
                  fareType: fareTypeLabelMap[activeDeal.fareType],
                  price: <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />,
                  strong: renderStrong,
                }}
              />
            </BodyText>
            {isFlightsActive && travelPeriodsText && (
              <BodyText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="AHNby_D9PEG6TMmhhNVE"
                  defaultMessage="Select dates <strong>{travelPeriods}</strong>"
                  description="Prompt to select dates within the flight deal travel periods"
                  values={{
                    travelPeriods: travelPeriodsText,
                    strong: renderStrong,
                  }}
                />
              </BodyText>
            )}
            {!isFlightsActive && (
              <TextLink size="small" colour="light-foreground-alt" weight="regular" onClick={handleToggleFlights}>
                {toggleFlightsText}
              </TextLink>
            )}
          </Group>
        </Group>
      </CSSBreakpoint>
    </>
  )

  if (wrapInHeaderExtensionContainer) {
    return <OfferCalendarHeaderExtensionContainer>{banner}</OfferCalendarHeaderExtensionContainer>
  }

  return banner
}

export default OfferCalendarDatePickerFlightDealBanner
